
import { Message } from "@/types/message";
import { format } from "date-fns";
import { formatForEmailAndClipboard } from "../formatters/messageFormatter";

/**
 * Helper to get the latest message from a thread group with the same relatedToMessageId
 */
export const getLatestVersionFromThread = (messages: Message[], threadId: string | null): Message | null => {
  if (!threadId) return null;
  
  const threadMessages = messages
    .filter(msg => msg.relatedToMessageId === threadId || msg.id === threadId)
    .sort((a, b) => {
      // Sort by version if available, otherwise by timestamp
      if (a.version && b.version) return b.version - a.version;
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
  
  return threadMessages.length > 0 ? threadMessages[0] : null;
};

/**
 * Filter messages to keep only the latest version from each thread
 */
export const filterToLatestVersions = (messages: Message[]): Message[] => {
  const result: Message[] = [];
  const processedThreadIds = new Set<string>();
  
  // First pass: collect all messages that aren't part of a thread
  messages.forEach(msg => {
    if (!msg.relatedToMessageId && !processedThreadIds.has(msg.id)) {
      // This is either a standalone message or the original message of a thread
      if (messages.some(m => m.relatedToMessageId === msg.id)) {
        // This is the original message of a thread, get the latest version
        const latestVersion = getLatestVersionFromThread(messages, msg.id);
        if (latestVersion) {
          result.push(latestVersion);
          processedThreadIds.add(msg.id);
        }
      } else {
        // This is a standalone message
        result.push(msg);
      }
    }
  });
  
  // Second pass: add all messages that are part of a thread but we haven't processed their thread yet
  messages.forEach(msg => {
    if (msg.relatedToMessageId && !processedThreadIds.has(msg.relatedToMessageId)) {
      const latestVersion = getLatestVersionFromThread(messages, msg.relatedToMessageId);
      if (latestVersion) {
        result.push(latestVersion);
        processedThreadIds.add(msg.relatedToMessageId);
      }
    }
  });
  
  // Sort by timestamp
  return result.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
};

/**
 * Formats message data for PDF export
 */
export const formatMessagesForPdf = (
  messages: Message[], 
  userFullName: string,
  merchantName: string
): string[][] => {
  const messageRows: string[][] = [];
  
  messages.forEach((message) => {
    // Format date
    const dateStr = format(message.timestamp, "MMM d, h:mm a");
    
    // Determine sender label based on message type
    let sender = "";
    if (message.type === "user") {
      sender = userFullName; // Use customer's full name instead of "You"
    } else if (message.type === "seller") {
      sender = merchantName || "Merchant";
    } else if (message.type === "ai") {
      sender = userFullName; // Use customer's full name instead of "AI Response"
    } else {
      sender = "System";
    }
    
    // Format content for readability
    const formattedContent = formatForEmailAndClipboard(message.content);
    
    // Add to rows
    messageRows.push([dateStr, sender, formattedContent]);
  });
  
  return messageRows;
};
